/**
 * Checklist documental de uma unidade. Cartão CNPJ, IE e registro ANP são
 * gerados pelo servidor sob demanda — abrem em nova aba, sem ocupar o banco.
 */

import { ExternalLink } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAtualizarDocumento, useDocumentos } from "@/hooks/queries";
import { api, baixar } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { Documento } from "@/types";

const VARIANTE = { OK: "ok", Pendente: "atencao", Vencido: "erro" } as const;

async function abrirDocumento(doc: Documento) {
  const blob = await api<Blob>(`/documentos/item/${doc.id}/arquivo`, { blob: true });
  const url = URL.createObjectURL(blob);
  const aba = window.open(url, "_blank", "noopener");
  if (!aba) baixar(blob, doc.arquivoNome || `${doc.nome}.pdf`);
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function DocumentosUnidade({ unidade }: { unidade: string | null }) {
  const { podeEditar } = useAuth();
  const { data, isLoading } = useDocumentos(unidade);
  const atualizar = useAtualizarDocumento(unidade);

  if (!unidade) return null;
  if (isLoading || !data) return <Skeleton className="h-64 w-full" />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Documentos · {data.unidade.cidade}/{data.unidade.uf}
        </CardTitle>
        <CardDescription>
          Cartão CNPJ, Inscrição Estadual e registro ANP são emitidos a partir da base cadastral —
          os demais são controlados manualmente.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        {data.documentos.map((doc) => (
          <div
            key={doc.id}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-border/70 px-3 py-2.5 transition-colors duration-200 hover:border-primary/30 hover:bg-accent/40"
          >
            <span className="flex-1 text-sm font-semibold">{doc.nome}</span>

            {podeEditar ? (
              <Select
                value={doc.status}
                onValueChange={(status) =>
                  atualizar.mutate({ id: doc.id, status: status as Documento["status"] })
                }
              >
                <SelectTrigger className="h-8 w-36 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["OK", "Pendente", "Vencido"] as const).map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Badge variant={VARIANTE[doc.status]}>{doc.status}</Badge>
            )}

            {doc.temArquivo && (
              <Button variant="outline" size="sm" onClick={() => abrirDocumento(doc)}>
                <ExternalLink />
                Abrir
              </Button>
            )}
            {!doc.temArquivo && doc.link && (
              <Button variant="outline" size="sm" asChild>
                <a href={doc.link} target="_blank" rel="noreferrer noopener">
                  <ExternalLink />
                  Link
                </a>
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
